import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Loader2 } from 'lucide-react';
import { ROUTES } from '@/app/routes';
import { useContentDetail } from '../api';
import { ContentMediaCard } from '../components/ContentMediaCard';
import { ContentSummaryCard } from '../components/ContentSummaryCard';
import { ContentEngagementPane } from '../components/ContentEngagementPane';
import { CreatorInfoCard } from '../components/CreatorInfoCard';
import { ModerationHistoryCard } from '../components/ModerationHistoryCard';
import { ContentReportsCard } from '../components/ContentReportsCard';
import { ContentModerationActionsCard } from '../components/ContentModerationActionsCard';
import { ContentCoopIntelligenceCard } from '../components/ContentCoopIntelligenceCard';
import { ContentDescriptionCard } from '../components/ContentDescriptionCard';
import { ContentModerationOverviewStrip } from '../components/ContentModerationOverviewStrip';

function Breadcrumb({ harness }: { harness?: boolean }) {
  const parentPath = harness ? ROUTES.pipelineTestPosts : ROUTES.content;
  const parentLabel = harness ? 'Pipeline Tests' : 'Content Management';
  return (
    <nav className="flex items-center gap-1 text-sm text-muted-foreground">
      <Link to={parentPath} className="hover:text-foreground">
        {parentLabel}
      </Link>
      <ChevronRight className="size-4" />
      <span className="text-foreground">Content Detail</span>
    </nav>
  );
}

export function ContentDetailPage() {
  const { contentId = '' } = useParams();
  const { detail, creator, creatorId, validId } = useContentDetail(contentId);

  if (!validId) {
    return (
      <div className="p-6">
        <Breadcrumb />
        <p className="mt-6 text-sm text-muted-foreground">Post not found.</p>
      </div>
    );
  }

  if (detail.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  if (detail.isError) {
    return (
      <div className="p-6">
        <Breadcrumb />
        <p className="mt-6 text-sm text-destructive">Failed to load content. Please try again.</p>
      </div>
    );
  }

  const data = detail.data;
  if (!data) {
    return (
      <div className="p-6">
        <Breadcrumb />
        <p className="mt-6 text-sm text-muted-foreground">Post not found.</p>
      </div>
    );
  }

  const harness = data.adminPipelineHarness === true;

  // Content is blurred/BW when not yet moderated (WAITING) or blocked (HIDDEN/TERMINATED).
  const isBlurred =
    data.postStatus === 'WAT' || data.postStatus === 'HID' || data.postStatus === 'TER';

  return (
    <div className="flex flex-col gap-4 p-4">
      <Breadcrumb harness={harness} />

      <ContentModerationOverviewStrip
        contentId={contentId}
        postStatus={data.postStatus ?? null}
        moderationStatus={data.moderationStatus ?? null}
        combinedRisk={
          (data as typeof data & { combinedRisk?: number | null }).combinedRisk ?? null
        }
        riskTier={(data as typeof data & { riskTier?: string | null }).riskTier ?? null}
        isAddCategory={data.isAddCategory ?? null}
        postProcessingError={data.postProcessingError ?? null}
        bedrockLastAttemptAt={data.bedrockLastAttemptAt ?? null}
        moderationLastError={data.moderationLastError ?? null}
        moderationStartedAt={data.moderationStartedAt ?? null}
        moderationDecidedAt={data.moderationDecidedAt ?? null}
        createdAt={data.createdAt ?? null}
      />

      {/* Content + sidebar — keep media/actions at the top for the moderation decision loop */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        <ContentMediaCard medias={data.medias ?? []} blurred={isBlurred} />

        <div className="flex flex-col gap-4">
          <ContentModerationActionsCard
            contentId={contentId}
            postStatus={data.postStatus}
            moderationStatus={data.moderationStatus}
            creatorId={creatorId}
            adminPipelineHarness={harness}
          />
          <CreatorInfoCard detail={data} creator={creator.data} creatorId={creatorId} />
          <ContentReportsCard contentId={contentId} postStatus={data.postStatus ?? null} />
        </div>
      </div>

      {/* Content Summary | Content Details — same row */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ContentSummaryCard contentId={contentId} detail={data} />
        <ContentDescriptionCard
          contentId={contentId}
          description={data.content}
          postStatus={data.postStatus}
          moderationStatus={data.moderationStatus}
        />
      </div>

      <ContentEngagementPane postId={Number(contentId)} />

      <ModerationHistoryCard
        contentId={contentId}
        combinedRisk={
          (data as typeof data & { combinedRisk?: number | null }).combinedRisk ?? null
        }
        riskTier={(data as typeof data & { riskTier?: string | null }).riskTier ?? null}
      />

      {/* Coop scan (snapshot) | Coop intelligence (deep dive) — side by side */}
      <ContentCoopIntelligenceCard
        contentId={contentId}
        postStatus={data.postStatus}
        moderationStatus={data.moderationStatus}
      />
    </div>
  );
}
